/* The process strip, scrubbed as a canvas image-sequence.
 *
 * This replaces a <video> whose currentTime was driven from scroll. That
 * approach was rebuilt many times — clip length, frame rate, resolution,
 * keyframe density, easing — and it stepped on every attempt. The reason is
 * in the Apex engine, which solved the same problem first:
 *
 *   "We do NOT scrub a <video> on mobile — seeking video.currentTime every
 *    frame stutters on phone decoders no matter how the clip is encoded.
 *    Decoded frames drawn to a canvas are random-access-free and
 *    butter-smooth. ONE engine for both viewports."
 *
 * A seek is a decoder round-trip: it lands on a keyframe, decodes forward,
 * and cannot be interrupted, so scroll input arrives faster than frames come
 * back. An <img> already decoded is just a drawImage. So: 120 webp frames per
 * strip, one canvas, and progress across a sticky track picks the frame.
 *
 * The frames are also smaller than the clip they replace — 1.0-1.9MB against
 * 4-6MB — and only a window around the cursor is ever fetched.
 */
(function () {
  'use strict';

  var IMG = '[class*="ai-bilona-process-image"]:not([class*="wrap"]),'
          + '[class*="ai-bilona-process-mobile-image"]';

  // Which frame set belongs to this page. Same ordering as the old video map:
  // the URL is the reliable signal, because a section's text is mostly its own
  // inlined CSS and the mobile stills are named Artboard_*.png.
  // buffalo comes first: its handle also contains "bilona", so a later test
  // would never be reached and the page would draw the cow strip.
  var SETS = [
    { test: /buffalo/i, key: 'buffalo' },
    { test: /moringa/i, key: 'moringa' },
    { test: /mirch|chilli|chili/i, key: 'mirch' },
    { test: /haldi|turmeric/i, key: 'haldi' }
    // default: bilona
  ];

  function pickSet(isMobile) {
    var map = window.jpStepsFrames;
    if (!map) return null;
    var hay = (location.pathname || '') + ' ' + (document.title || '');
    var key = 'bilona';
    for (var i = 0; i < SETS.length; i++) {
      if (SETS[i].test.test(hay)) { key = SETS[i].key; break; }
    }
    var set = map[key] || map.bilona;
    if (!set) return null;
    return isMobile ? set.mobile : set.desktop;
  }

  function build(img) {
    if (img.__jpCine) return;
    var isMobile = /mobile/.test(img.className);
    var set = pickSet(isMobile);
    if (!set || !set.sheet || !set.count) return;
    img.__jpCine = true;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var track = document.createElement('div');
    track.className = 'jp-cine-track jp-cine-track--' + (isMobile ? 'mobile' : 'desktop');
    var pin = document.createElement('div');
    pin.className = 'jp-cine-pin';
    var cv = document.createElement('canvas');
    cv.className = 'jp-cine-canvas';
    cv.setAttribute('aria-hidden', 'true');   // the <img> alt already describes it

    img.parentNode.insertBefore(track, img);
    track.appendChild(pin);
    pin.appendChild(cv);
    img.classList.add('jp-cine-still');
    img.style.display = 'none';

    var N = set.count;
    var drawn = -1;
    var ctx = cv.getContext('2d', { alpha: false });
    var sized = false;

    // The canvas is opaque, so it has to paint the same colour the section
    // does. Walk up for the nearest ancestor that actually paints one; the
    // block sits on #faf8f5 and a white canvas read as a slab on the cream.
    var bg = '#faf8f5';
    for (var n = track.parentElement; n; n = n.parentElement) {
      var c = getComputedStyle(n).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { bg = c; break; }
    }

    // One sprite sheet, not 120 files.
    //
    // The frames used to be separate assets, and pushing 960 of them was not
    // possible against this store: the CLI uploads about five concurrently
    // and that reliably trips a TLS "bad record mac", while one file at a
    // time always succeeds and would take hours. A 10x12 grid is ONE asset
    // per strip — one upload, one fetch, one decode — and the whole set is
    // under a megabyte more than the clip it replaces.
    var COLS = 10, ROWS = 12;
    var sheet = null, tileW = 0, tileH = 0;

    function size() {
      var r = cv.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      // The frames are authored at 1400px (desktop) / 860px (phone), so a
      // higher cap has real detail to draw rather than upscaling.
      var dpr = Math.min(3, window.devicePixelRatio || 1);
      var w = Math.round(r.width * dpr), h = Math.round(r.height * dpr);
      // Setting either dimension CLEARS the bitmap, so anything already
      // painted is gone and has to be painted again. Marking drawn = -1 is
      // what makes the next draw() actually repaint instead of returning
      // early as "same frame" — and the resize listener has to come back
      // through draw(), which it does via onScroll().
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; drawn = -1; }
      sized = true;
      return true;
    }

    function draw(i) {
      if (!sheet || !sheet.complete || !sheet.naturalWidth) return;
      // Size FIRST. Setting canvas.width clears the bitmap, so sizing after
      // the `i === drawn` check meant the very first draw was skipped as
      // "already drawn" while the canvas it had been drawn on no longer
      // existed — the section rendered as an empty cream box.
      if (!sized && !size()) return;
      if (i === drawn) return;
      var cw = cv.width, ch = cv.height;
      // contain: the strip is artwork, never crop it
      var s = Math.min(cw / tileW, ch / tileH);
      var dw = tileW * s, dh = tileH * s;
      // Fill with the SECTION's background, read from the DOM, not white. The
      // generated block sits on #faf8f5, so a white canvas showed as a pale
      // slab against the cream — "background aur page ka colour same rakho".
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, cw, ch);
      // Source rect = this frame's tile in the grid.
      ctx.drawImage(sheet,
        (i % COLS) * tileW, Math.floor(i / COLS) * tileH, tileW, tileH,
        (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      drawn = i;
    }

    // The frame the scroll position currently asks for, so a sheet that
    // arrives late still paints whatever is wanted by then.
    var wanted = 0;
    function loadSheet(onReady) {
      sheet = new Image();
      sheet.decoding = 'async';
      sheet.onload = function () {
        tileW = sheet.naturalWidth / COLS;
        tileH = sheet.naturalHeight / ROWS;
        onReady();
        draw(wanted);
      };
      sheet.src = set.sheet;
    }

    function progress() {
      var tr = track.getBoundingClientRect();
      var span = Math.max(1, tr.height - window.innerHeight);
      var p = (0 - tr.top) / span;
      return Math.max(0, Math.min(1, p));
    }

    var raf = 0;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = 0;
        var p = reduce ? 1 : progress();
        var i = Math.round(p * (N - 1));
        wanted = i;
        draw(i);
      });
    }

    // Fetch the sheet only when this track is the one being shown.
    //
    // Both strips are in the markup and CSS hides whichever does not belong at
    // this width — but a hidden element's script still ran, so a phone was
    // downloading the desktop sheet as well as its own. Measured on the live
    // PDP under iPhone emulation: 9.4MB total, 6.2MB of it the two sheets,
    // half of that for a strip the phone never shows.
    //
    // The pin has `display:none` at the wrong width (see jp-bilona-reveal.css),
    // so offsetParent is the honest test for "is this one live" — it does not
    // care which media query did the hiding.
    var started = false;
    function startWhenVisible() {
      if (started || !pin.offsetParent) return;
      started = true;
      track.classList.add('jp-cine-track--waiting');
      loadSheet(function () {
        track.classList.remove('jp-cine-track--waiting');
        sized = false;
        size();
        onScroll();
      });
    }
    // Deferred to the end of the sweep — see the note there. Calling it here
    // would read the layout before the hidden strip has been marked hidden.
    pending.push(startWhenVisible);
    // A rotation can make the other strip the live one; pick it up then.
    window.addEventListener('resize', startWhenVisible, { passive: true });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { sized = false; drawn = -1; onScroll(); }, { passive: true });
    onScroll();
  }

  // Build every track, THEN mark which are hidden, THEN start loading.
  //
  // These were one pass, and the order was the bug: build() decides whether to
  // fetch its sheet by asking whether its pin is displayed, but the class that
  // hides the desktop pin on a phone was applied after every build() had
  // already run. So the desktop strip still looked live at that instant and
  // fetched — measured 2.0MB of desktop sheet on an iPhone that never shows it.
  var pending = [];
  function sweep() {
    [].slice.call(document.querySelectorAll(IMG)).forEach(build);
    // Only hide the desktop strip on a phone when a mobile one really exists.
    var hasMobile = !!document.querySelector('.jp-cine-track--mobile');
    [].slice.call(document.querySelectorAll('.jp-cine-track--desktop')).forEach(function (t) {
      t.classList.toggle('jp-cine-track--has-mobile', hasMobile);
    });
    // Now the layout tells the truth about which strip is on screen. Drain the
    // queue rather than iterate it: sweep() runs again on DOMContentLoaded and
    // load, and a start that has already fired must not be re-queued.
    var queued = pending;
    pending = [];
    queued.forEach(function (start) { start(); });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();
